import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPolicies, getSubscribers } from '../../../redux/policiesReducer';
import request from '../../../helpers/request';
import { listFiles } from '../../../helpers/storage';

export default function useMentionData() {
  const dispatch = useDispatch();

  const policies = useSelector((state) => state.policies.policies);
  const policiesLoading = useSelector((state) => state.policies.isPoliciesLoading);
  const subscribers = useSelector((state) => state.policies.subscribers);
  const subscribersLoading = useSelector((state) => state.policies.isSubscribersLoading);

  const [mentionTasks, setMentionTasks] = useState([]);
  const [mentionTasksLoading, setMentionTasksLoading] = useState(false);
  const [mentionTasksPage, setMentionTasksPage] = useState(1);
  const [mentionTasksHasMore, setMentionTasksHasMore] = useState(false);

  const [mentionDocuments, setMentionDocuments] = useState([]);
  const [mentionDocumentFolders, setMentionDocumentFolders] = useState([]);
  const [mentionDocumentsLoading, setMentionDocumentsLoading] = useState(false);
  const [mentionDocumentBreadcrumbs, setMentionDocumentBreadcrumbs] = useState([]);

  const [mentionFunctions, setMentionFunctions] = useState([]);
  const [mentionFunctionsLoading, setMentionFunctionsLoading] = useState(false);

  const handleLoadPolicies = useCallback(() => {
    if (!policies.length && !policiesLoading) {
      dispatch(getPolicies());
    }
  }, [dispatch, policies.length, policiesLoading]);

  const handleLoadTasks = useCallback(async () => {
    if (mentionTasksLoading) return;
    setMentionTasksLoading(true);
    try {
      const response = await request.get('/api/v1/tools/task/', {
        params: { page: 1, page_size: 20 }
      });
      const results = response.data.results || [];
      setMentionTasks(results);
      setMentionTasksPage(2);
      setMentionTasksHasMore(!!response.data.next);
    } catch (e) {
      console.error('Failed to load tasks:', e);
    } finally {
      setMentionTasksLoading(false);
    }
  }, [mentionTasksLoading]);

  const handleLoadMoreTasks = useCallback(async () => {
    if (mentionTasksLoading || !mentionTasksHasMore) return;
    setMentionTasksLoading(true);
    try {
      const response = await request.get('/api/v1/tools/task/', {
        params: { page: mentionTasksPage, page_size: 20 }
      });
      const results = response.data.results || [];
      setMentionTasks((prev) => [...prev, ...results]);
      setMentionTasksPage((prev) => prev + 1);
      setMentionTasksHasMore(!!response.data.next);
    } catch (e) {
      console.error('Failed to load more tasks:', e);
    } finally {
      setMentionTasksLoading(false);
    }
  }, [mentionTasksLoading, mentionTasksHasMore, mentionTasksPage]);

  const buildDocumentBreadcrumbs = useCallback((prefix) => {
    if (!prefix) return [];
    const parts = prefix.split('/').filter(Boolean);
    return parts.map((part, index) => ({
      name: part,
      path: parts.slice(0, index + 1).join('/') + '/',
    }));
  }, []);

  const handleLoadDocuments = useCallback(async (prefix = '') => {
    setMentionDocumentsLoading(true);
    try {
      const response = await listFiles(prefix);
      const data = response.data;
      setMentionDocuments(data.files || []);
      setMentionDocumentFolders(data.folders || []);
      setMentionDocumentBreadcrumbs(buildDocumentBreadcrumbs(prefix));
    } catch (e) {
      console.error('Failed to load documents:', e);
    } finally {
      setMentionDocumentsLoading(false);
    }
  }, [buildDocumentBreadcrumbs]);

  const handleNavigateDocumentFolder = useCallback((folderPath) => {
    const normalizedPath = folderPath.startsWith('user_uploads/')
      ? folderPath.slice('user_uploads/'.length)
      : folderPath;
    handleLoadDocuments(normalizedPath);
  }, [handleLoadDocuments]);

  const handleNavigateDocumentRoot = useCallback(() => {
    handleLoadDocuments('');
  }, [handleLoadDocuments]);

  const handleLoadFunctions = useCallback(async () => {
    if (mentionFunctionsLoading) return;
    setMentionFunctionsLoading(true);
    try {
      const response = await request.get('/api/v1/functions/');
      setMentionFunctions(response.data || []);
    } catch (e) {
      console.error('Failed to load functions:', e);
    } finally {
      setMentionFunctionsLoading(false);
    }
  }, [mentionFunctionsLoading]);

  const handleLoadSubscribers = useCallback(() => {
    if (!subscribers.length && !subscribersLoading) {
      dispatch(getSubscribers());
    }
  }, [dispatch, subscribers.length, subscribersLoading]);

  return {
    policies,
    policiesLoading,
    handleLoadPolicies,
    mentionTasks,
    mentionTasksLoading,
    mentionTasksHasMore,
    handleLoadTasks,
    handleLoadMoreTasks,
    mentionDocuments,
    mentionDocumentFolders,
    mentionDocumentsLoading,
    mentionDocumentBreadcrumbs,
    handleLoadDocuments,
    handleNavigateDocumentFolder,
    handleNavigateDocumentRoot,
    mentionFunctions,
    mentionFunctionsLoading,
    handleLoadFunctions,
    subscribers,
    subscribersLoading,
    handleLoadSubscribers,
  };
}
